<template>
  <article
    class="social-media"
    :class="{
      'social-media--inline': inline,
      'social-media--light': light,
    }"
  >
    <header class="social-media__header">
      <p v-if="!light" class="social-media__eyebrow">Suivez-nous</p>
      <h5 class="jaapokki">{{ title }}</h5>
      <p v-if="!light" class="social-media__copy">
        Retrouvez nos actualites, nos temps forts et les coulisses de l'institut
        sur vos reseaux preferes.
      </p>
    </header>

    <div class="social-media__grid">
      <a
        v-for="network in networks"
        :key="network.label"
        :href="network.href"
        class="social-media__link"
        :aria-label="`Suivre l'Institut Taxi sur ${network.label}`"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span class="social-media__icon" aria-hidden="true">
          <i :class="network.icon"></i>
        </span>
        <span v-if="!light" class="social-media__label">
          <strong>{{ network.label }}</strong>
          <span>{{ network.handle }}</span>
        </span>
      </a>
    </div>
  </article>
</template>

<script>
const networks = [
  {
    label: "Facebook",
    handle: "ITTI.92",
    href: "https://www.facebook.com/ITTI.92",
    icon: "fab fa-facebook-f",
  },
  {
    label: "Instagram",
    handle: "@institut.taxi",
    href: "https://www.instagram.com/institut.taxi/",
    icon: "fab fa-instagram",
  },
  {
    label: "Twitter",
    handle: "@InstitutTaxi",
    href: "https://twitter.com/InstitutTaxi",
    icon: "fab fa-twitter",
  },
  {
    label: "Snapchat",
    handle: "snap.wss",
    href: "https://www.snapchat.com/add/snap.wss",
    icon: "fab fa-snapchat-ghost",
  },
  {
    label: "LinkedIn",
    handle: "Institut Taxi",
    href: "https://www.linkedin.com/company/institut-taxi",
    icon: "fab fa-linkedin-in",
  },
  {
    label: "Pinterest",
    handle: "itti92",
    href: "https://www.pinterest.fr/itti92",
    icon: "fab fa-pinterest",
  },
];

export default {
  name: "SocialMedia",
  props: {
    inline: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Suivez-nous",
    },
  },
  data() {
    return {
      networks,
    };
  },
};
</script>

<style lang="scss" scoped>
.social-media {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  height: 100%;
  padding: clamp(24px, 2.8vw, 34px);
  border-radius: 28px;
  background: $color-primary;
  color: $color-text-light;
  box-shadow: 0 22px 44px rgba(0, 42, 84, 0.18);
  border: 1px solid rgba(245, 236, 236, 0.08);

  &--light {
    padding: 0;
    border-radius: 0;
    background: transparent;
    color: $color-text-light;
    box-shadow: none;
    border: 0;
  }

  &__header {
    display: grid;
    gap: 10px;
  }

  &__eyebrow {
    margin: 0;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(245, 236, 236, 0.72);
  }

  & h5 {
    margin: 0;
    color: inherit;
    font-size: clamp(1.45rem, 1.2rem + 0.4vw, 1.8rem);
    line-height: 1.05;
  }

  &__copy {
    margin: 0;
    font-size: 0.98rem;
    line-height: 1.6;
    color: rgba(245, 236, 236, 0.82);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  &--inline &__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &--light &__grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 74px;
    padding: 14px 16px;
    border-radius: 20px;
    background: rgba(245, 236, 236, 0.06);
    border: 1px solid rgba(245, 236, 236, 0.08);
    text-decoration: none;
    transition: transform 0.22s ease, background-color 0.22s ease,
      border-color 0.22s ease, box-shadow 0.22s ease;

    &:hover,
    &:focus-visible {
      transform: translateY(-2px);
      background: rgba(254, 185, 20, 0.16);
      border-color: rgba(254, 185, 20, 0.34);
      box-shadow: 0 16px 28px rgba(0, 0, 0, 0.18);
      outline: none;
    }
  }

  &--light &__link {
    justify-content: center;
    min-height: 0;
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: $color-secondary;
    color: $color-primary;
    flex: 0 0 auto;

    & i {
      font-size: 1.1rem;
    }
  }

  &--light &__icon {
    width: 42px;
    height: 42px;
    border-radius: 14px;
  }

  &__label {
    display: grid;
    gap: 4px;
    min-width: 0;

    & strong {
      font-size: 0.92rem;
      line-height: 1.2;
      color: $color-text-light;
    }

    & span {
      font-size: 0.9rem;
      line-height: 1.3;
      color: rgba(245, 236, 236, 0.76);
      overflow-wrap: anywhere;
    }
  }
}

@media screen and (max-width: 767px) {
  .social-media {
    padding: 22px;
    border-radius: 24px;

    &__grid,
    &--inline .social-media__grid {
      grid-template-columns: 1fr;
    }

    &--light {
      padding: 0;
      border-radius: 0;
    }
  }
}
</style>
